import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface Profile {
  id: string;
  email: string | null;
  plan: "free" | "pro";
  plan_status: "pending" | "active" | "cancelled";
  billing_period: "monthly" | "lifetime" | null;
  stripe_session_id: string | null;
  created_at: string;
  avatar_url: string | null;
  display_name: string | null;
  phone: string | null;
  two_fa_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isRecovery: boolean;
  pendingTwoFA: boolean;
  twoFaEmail: string | null;
  clearRecovery: () => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; requires2FA: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  verifySignupOtp: (email: string, token: string) => Promise<{ error: Error | null }>;
  verify2FA: (email: string, token: string) => Promise<{ error: Error | null }>;
  send2FAOTP: () => Promise<{ error: Error | null }>;
  resendOtp: (email: string, type: "signup" | "2fa") => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  const [pendingTwoFA, setPendingTwoFA] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState<string | null>(null);

  const fetchProfile = async (userId: string, userEmail?: string | null) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (data) {
      setProfile(data as Profile);
      return;
    }

    const { data: created } = await supabase
      .from("profiles")
      .insert({
        id: userId,
        email: userEmail ?? null,
        plan: "free",
        plan_status: "pending",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        two_fa_enabled: false,
      })
      .select("*")
      .maybeSingle();

    setProfile(created as Profile | null);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id, user.email);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) fetchProfile(s.user.id, s.user.email);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (_event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
      }
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id, s.user.email);
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const clearRecovery = () => setIsRecovery(false);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error as Error, requires2FA: false };

    const { data: profileData } = await supabase
      .from("profiles")
      .select("two_fa_enabled")
      .eq("id", data.user.id)
      .maybeSingle();

    if (profileData?.two_fa_enabled) {
      setTwoFaEmail(email);
      setPendingTwoFA(true);
      await supabase.auth.signOut();
      return { error: null, requires2FA: true };
    }

    return { error: null, requires2FA: false };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error: error as Error | null };
  };

  const verifySignupOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "signup" });
    return { error: error as Error | null };
  };

  const verify2FA = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "email" });
    if (!error) {
      setPendingTwoFA(false);
      setTwoFaEmail(null);
    }
    return { error: error as Error | null };
  };

  const send2FAOTP = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-2fa-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email: twoFaEmail }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.error) {
        return { error: new Error(data.error || "Failed to send code") };
      }
      return { error: null };
    } catch {
      return { error: new Error("Failed to send code. Please try again.") };
    }
  };

  const resendOtp = async (email: string, type: "signup" | "2fa") => {
    if (type === "signup") {
      const { error } = await supabase.auth.resend({ type: "signup", email });
      return { error: error as Error | null };
    }
    // 2FA resend via edge function
    try {
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-2fa-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.error) {
        return { error: new Error(data.error || "Failed to resend code") };
      }
      return { error: null };
    } catch {
      return { error: new Error("Failed to resend code. Please try again.") };
    }
  };

  const signOut = async () => {
    setPendingTwoFA(false);
    setTwoFaEmail(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user, session, profile, loading, isRecovery, pendingTwoFA, twoFaEmail,
        clearRecovery,
        signIn, signUp, signOut, refreshProfile,
        verifySignupOtp, verify2FA, send2FAOTP, resendOtp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
